import type { BusConfig } from '../config/bus.config';
import type { WorldConfig } from '../config/world.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import { RIDE_ONLY_TAG } from '../level/generation/LevelGenerator';
import { COLLECTIBLE_FLOAT_HEIGHT, laneCenterX } from '../level/segments/segmentBuilders';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import type { CollectibleManager } from '../collectibles/CollectibleManager';
import type { ObstacleInstance } from '../obstacles/ObstacleInstance';
import type { ObstacleManager } from '../obstacles/ObstacleManager';
import { aabb } from '../physics/Aabb';
import type { PlayerController } from '../player/PlayerController';
import { PlayerState } from '../player/PlayerState';
import type { ScoreManager } from '../scoring/ScoreManager';
import { Random } from '../utils/Random';
import { BusSeat, CargoLoss, crashLoss, landingLoss, landingQuality } from './busRules';
import { BusPhase, BusRide } from './BusRide';
import { BusTraffic, TrafficIncident, type TrafficCar, type TrafficView } from './traffic/BusTraffic';

export interface BusManagerDeps {
  readonly player: PlayerController;
  readonly obstacles: ObstacleManager;
  /** The bottles of the road modules inside the ride (those over removed obstacles come down to driving height). */
  readonly collectibles: CollectibleManager;
  readonly score: ScoreManager;
  readonly events: EventBus<GameEvents>;
  readonly config: BusConfig;
  readonly world: WorldConfig;
  /** Ground height at a track position (ramps), null where there is none. */
  readonly groundHeightAt: (distance: number, lateral: number) => number | null;
  /** End of the committed track (an undecided crossing ahead) – the traffic never comes from behind it. */
  readonly roadEnd: () => number;
  /** Seed of the run – the traffic of a ride is reproducible. */
  readonly seed: () => number;
  /** Extra lives a bonus may add at most (like the quiz bonus). */
  readonly maxExtraLives: number;
}

/** How the van met another vehicle. */
export const BusCrash = {
  /** Oncoming traffic, head-on. */
  HeadOn: 'HEAD_ON',
  /** The rival rammed the van from the side. */
  Side: 'SIDE',
  /** The van steered into the rival (a hit for him). */
  Shove: 'SHOVE',
  /** A bump in line: the rival rams the van's tail, or the van runs into him braking in front of it. */
  Rear: 'REAR',
} as const;
export type BusCrash = (typeof BusCrash)[keyof typeof BusCrash];

/** How a rival's attempt ended. */
export const RivalOutcome = {
  Shoved: 'SHOVED',
  Crashed: 'CRASHED',
  Passed: 'PASSED',
} as const;
export type RivalOutcome = (typeof RivalOutcome)[keyof typeof RivalOutcome];

/** A drop below the van larger than this (m) lifts it off – the lip of a ramp, not the end of a slope sample. */
const LIP = 0.06;
const EPSILON = 1e-6;
/** Seed stream of the ride's traffic. */
const TRAFFIC_STREAM = 77;
/** Obstacles are swept off the road this far ahead of the van (m). */
const SWEEP_AHEAD = 150;
/** Bodies closer than this side by side (m) touch – a crash (the traffic never drives into the van). */
const TOUCH = 0.12;
/** Steepest climb a ramp gives (rise per meter) – caps the climb rate the van carries off a lip. */
const MAX_CLIMB = 0.25;

/**
 * The ructec-Bulli in play – pure gameplay, stepped after the player and before the RunDirector (like the dozer):
 *
 *   waiting   the van idles in SEG_BUS_START_40M. When the runner reaches its rear – in any lane – it gets in (DRIVING,
 *             BusSeat): the PlayerController steers it with the lane logic, turns at crossings, the speed rises with
 *             the meters driven. The road belongs to the traffic: no obstacle is left on it (cleared as modules spawn,
 *             swept ahead of the van every step – an obstacle would throw the runner out).
 *   driving   the fight (BusTraffic, crash): the rival presses the van sideways, the van blocks him or steers into him –
 *             three hits and he is off the road; pressed too long the van is shoved a lane over, off the road's edge it
 *             tips over and blinks. Oncoming traffic now and then goes for the van. Every crash costs load (crashLoss – most when rammed in the air), so does a
 *             slanted landing (landingLoss) – but nothing ends the ride early. After BusConfig.rideLength m the runner hops out
 *             and runs on; every piece still on the roof pays bottles.
 *   done      the empty van brakes to a stop; the moving traffic turns off.
 */
export class BusManager {
  private ride: BusRide | null = null;
  private readonly seat: BusSeat;
  private readonly body = aabb();
  readonly traffic: BusTraffic;
  private readonly lanes: number[];
  private readonly nearby: ObstacleInstance[] = [];
  /** Seconds until the next crash counts / the next hit on the rival counts. */
  private grace = 0;
  private shoveGrace = 0;
  /** The car touching the van in the last step (id), how far the rival's pressing has got (0 … 1, 1 shoves the van). */
  private touching: number | null = null;
  private pushMeter = 0;
  /** Tipped over: seconds of blinking left, and to which side it fell. */
  private tipT = 0;
  private tipSide: -1 | 1 = 1;
  /** Run distances of the ride stretch (start module end … + ride length): no obstacles there. */
  private window: { from: number; to: number } | null = null;

  constructor(private readonly deps: BusManagerDeps) {
    this.seat = new BusSeat(deps.config);
    this.traffic = new BusTraffic({ config: deps.config.traffic, world: deps.world, groundHeightAt: deps.groundHeightAt });
    this.lanes = new Array<number>(deps.world.laneCount).fill(0);
    // Subscribed after the ObstacleManager: a module's obstacles exist when its segment spawns.
    deps.events.on('segment:spawned', ({ segment }) => this.spawn(segment));
  }

  /** The current ride (waiting, driving or braking), or null. */
  get current(): BusRide | null {
    return this.ride;
  }

  /** The ride the runner drives, or null. */
  get active(): BusRide | null {
    return this.ride?.phase === BusPhase.Driving ? this.ride : null;
  }

  get vehicleSeat(): BusSeat {
    return this.seat;
  }

  /** Tipped over: seconds of blinking left (0: on its wheels) and the side it fell to (BusPresenter). */
  get tipped(): { readonly remaining: number; readonly side: -1 | 1 } {
    return { remaining: this.tipT, side: this.tipSide };
  }

  /** How far the rival's pressing has got (0 … 1) – HUD. */
  get pressure(): number {
    return this.pushMeter;
  }

  /** Warning per lane 0 … 1 of the rival coming from behind (HUD) – zeros without a ride. */
  rearWarnings(): readonly number[] {
    const ride = this.active;
    if (!ride) return this.lanes.fill(0);
    return this.traffic.warnings(this.view(ride), this.lanes);
  }

  /** Fixed step, after the player moved and before the RunDirector checks its contacts. */
  step(dt: number): void {
    const ride = this.ride;
    if (!ride) return;
    ride.keepPrevious();
    switch (ride.phase) {
      case BusPhase.Waiting:
        this.tryMount(ride);
        break;
      case BusPhase.Driving:
        this.drive(ride, dt);
        break;
      case BusPhase.Done:
        this.brake(ride, dt);
        this.traffic.step(dt, { ...this.view(ride), calm: true });
        break;
    }
  }

  reset(): void {
    this.ride = null;
    this.window = null;
    this.seat.driven = 0;
    this.traffic.clear();
    this.tipT = 0;
    this.pushMeter = 0;
  }

  private spawn(segment: SegmentInstance): void {
    const setPiece = segment.definition.bus;
    const { world, obstacles, config } = this.deps;
    if (setPiece) {
      const distance = segment.startDistance + setPiece.parkZ;
      this.ride = new BusRide(segment, setPiece.lane, { distance, x: laneCenterX(world, setPiece.lane), y: world.groundHeight }, config.cargo.pieces);
      const ride = segment.definition.generation.ride;
      this.window = ride ? { from: segment.endDistance, to: segment.endDistance + ride.length } : null;
      return;
    }
    const w = this.window;
    if (!w || segment.startDistance < w.from - EPSILON || segment.startDistance >= w.to) return;
    if (segment.definition.generation.tags.includes(RIDE_ONLY_TAG)) return;
    // curves, crossings, the gantry: nothing on the road inside the ride – out of play AND out of sight (the runner
    // would drive through it, a hit would throw him out)
    for (const obstacle of obstacles.forSegment(segment.uid)) obstacles.clearObstacle(obstacle);
    // their bottles hung over those obstacles (jump arcs, container roofs): down to driving height – reachable by the van
    this.deps.collectibles.lowerFloating(segment.uid, COLLECTIBLE_FLOAT_HEIGHT);
  }

  /** Every step: whatever obstacle is on the road ahead of the van inside the ride stretch goes (branch previews too). */
  private sweep(ride: BusRide): void {
    const w = this.window;
    if (!w) return;
    const { obstacles } = this.deps;
    const from = Math.max(w.from, ride.pose.distance - 10);
    const to = Math.min(w.to, ride.pose.distance + SWEEP_AHEAD);
    if (to <= from) return;
    obstacles.query(from, to, this.nearby);
    for (const obstacle of this.nearby) obstacles.clearObstacle(obstacle);
  }

  // ── Waiting ──────────────────────────────────────────────────────────────

  private tryMount(ride: BusRide): void {
    const { player, config, events } = this.deps;
    const seatDistance = ride.pose.distance + config.seat.z;
    const rear = seatDistance - config.body.rear;
    if (player.current.distance < rear) return;
    if (player.current.distance > seatDistance + config.body.front + 2) {
      ride.phase = BusPhase.Done; // passed it (e.g. thrown back by a hit at the moment): the road stays calm anyway
      return;
    }
    if (player.vehicle || player.isIncapacitated) return;
    this.seat.driven = 0;
    player.mount(this.seat, { distance: seatDistance, lane: ride.lane, ground: ride.pose.y });
    if (player.vehicle !== this.seat) return;
    ride.phase = BusPhase.Driving;
    ride.startDistance = seatDistance;
    this.grace = 0;
    this.shoveGrace = 0;
    this.touching = null;
    this.pushMeter = 0;
    this.tipT = 0;
    this.traffic.start(Random.deriveSeed(this.deps.seed(), TRAFFIC_STREAM));
    this.sweep(ride);
    events.emit('bus:mounted', { ride });
  }

  // ── Driving ──────────────────────────────────────────────────────────────

  private drive(ride: BusRide, dt: number): void {
    const { player, config, events } = this.deps;
    if (player.vehicle !== this.seat || player.state !== PlayerState.Driving) {
      this.finish(ride, false, false); // the run ended in it
      return;
    }
    const seatDistance = player.current.distance;
    const pose = ride.pose;
    this.seat.driven = Math.max(0, seatDistance - ride.startDistance);
    const moved = seatDistance - config.seat.z - pose.distance;
    pose.distance = seatDistance - config.seat.z;
    pose.x = player.current.x;
    pose.speed = player.speed;
    pose.travelled += Math.max(0, moved);

    this.sweep(ride);
    this.vertical(ride, dt);
    player.current.y = pose.y;

    this.tipT = Math.max(0, this.tipT - dt);
    this.traffic.step(dt, this.view(ride));
    for (const car of this.traffic.newWarnings) events.emit('bus:rivalComing', { ride, car });
    for (const { incident, car } of this.traffic.incidents) {
      if (incident === TrafficIncident.Attack) events.emit('bus:oncomingAttack', { ride, car });
      else this.rivalOut(ride, car, incident === TrafficIncident.Passed ? RivalOutcome.Passed : RivalOutcome.Crashed);
    }
    this.crash(ride, dt);
    if (this.seat.driven >= config.rideLength && !pose.airborne) this.finish(ride, true, true);
  }

  private view(ride: BusRide): TrafficView {
    const { config, player, roadEnd } = this.deps;
    const driven = this.seat.driven;
    return {
      busDistance: ride.pose.distance,
      busX: ride.pose.x,
      busHalfWidth: config.body.width / 2,
      busLateral: player.current.lateralVelocity,
      busSpeed: ride.pose.speed,
      busLane: player.currentLane,
      busFront: config.seat.z + config.body.front,
      busRear: config.body.rear - config.seat.z,
      share: Math.min(1, driven / config.rideLength),
      calm: driven < config.traffic.calmStart || driven > config.rideLength - config.traffic.calmEnd,
      roadEnd: roadEnd(),
    };
  }

  /** Road height under the van (ramps) – off a lip it flies at its climb rate until it touches down. */
  private vertical(ride: BusRide, dt: number): void {
    const { config, world, groundHeightAt, events } = this.deps;
    const pose = ride.pose;
    const ground = groundHeightAt(pose.distance + config.seat.z, pose.x) ?? world.groundHeight;
    if (pose.airborne) {
      ride.vy -= config.gravity * dt;
      pose.y += ride.vy * dt;
      if (pose.y <= ground) this.land(ride, ground);
      return;
    }
    if (ground < pose.y - LIP && ride.vy > 0) {
      pose.airborne = true; // off the lip: on at the climb rate it had on the ramp
      pose.y += ride.vy * dt;
      events.emit('bus:airborne', { ride });
      return;
    }
    // the climb rate it carries off a lip: at most what a ramp gives at this speed – steered sideways onto a ramp (they
    // lie on lanes 1–2 only) the height steps up at once, that is no launch
    ride.vy = dt > 0 ? Math.min((ground - pose.y) / dt, pose.speed * MAX_CLIMB) : 0;
    pose.y = ground;
  }

  private land(ride: BusRide, ground: number): void {
    const { config, events, player } = this.deps;
    const impactSpeed = -ride.vy;
    ride.pose.airborne = false;
    ride.pose.y = ground;
    ride.vy = 0;
    const quality = landingQuality(config, player.current.lateralVelocity);
    events.emit('bus:landed', { ride, quality, impactSpeed });
    this.loseCargo(ride, landingLoss(config.cargo, quality, ride.cargo), CargoLoss.Landing);
  }

  /**
   * The fight (user 2026-10-05: "ein echter Kampf um den Platz"). The van's body touches a car:
   *   oncoming   head-on – it spins off, the load pays.
   *   tail/nose  the rival rams the van's tail (blocked) or the van runs into him braking in front of it – a crash.
   *   flank      steered into him (sideways towards him): he takes a hit (3 and he is off the road), the van bounces
   *              back into its lane. Otherwise he presses: the touch is a crash, and pressed for `shoveAfter` s the van
   *              is shoved a lane over – at the edge of the road it tips over (tip).
   * Every crash costs load (crashLoss – most when rammed in the air).
   */
  private crash(ride: BusRide, dt: number): void {
    const { player, config, events } = this.deps;
    const r = config.traffic.rival;
    this.grace = Math.max(0, this.grace - dt);
    this.shoveGrace = Math.max(0, this.shoveGrace - dt);
    if (this.tipT > 0) return; // blinking after tipping over: nothing counts
    player.colliderOf('current', this.body);
    this.body.minX -= TOUCH;
    this.body.maxX += TOUCH;
    this.body.minZ -= TOUCH;
    this.body.maxZ += TOUCH;
    const car = this.traffic.contact(this.body);
    if (!car) {
      this.touching = null;
      this.pushMeter = Math.max(0, this.pushMeter - dt * 2);
      return;
    }
    const first = this.touching !== car.id;
    this.touching = car.id;
    const airborne = ride.pose.airborne;
    const dx = car.pose.x - ride.pose.x;
    const toward = Math.sign(dx) || 1;

    if (car.dir < 0) {
      if (!first) return;
      this.traffic.wreck(car, ride.pose.x);
      this.crashed(ride, car, BusCrash.HeadOn, airborne);
      return;
    }
    // in line with the van: a tail or nose bump
    if (Math.abs(dx) < config.body.width / 2 + car.width / 2 - 0.3) {
      if (!first || this.grace > 0) return;
      this.traffic.bump(car, this.view(ride));
      if (car.pressing) this.traffic.endAttack(car);
      this.crashed(ride, car, BusCrash.Rear, airborne);
      return;
    }
    // flank: steered into him – a hit for him
    if (player.current.lateralVelocity * toward > r.shoveLateral && this.shoveGrace <= 0) {
      this.shoveGrace = 0.35;
      this.pushMeter = 0;
      const out = this.traffic.hit(car, ride.pose.x);
      player.bounceBack();
      // the van's own hit costs no load – the fight would empty the roof long before the end (his presses do cost)
      this.deps.events.emit('bus:crashed', { ride, car, kind: BusCrash.Shove, airborne });
      if (out) this.rivalOut(ride, car, RivalOutcome.Shoved);
      else events.emit('bus:rivalHit', { ride, car, hits: car.hits, toughness: r.toughness });
      return;
    }
    // he presses against the van
    if (first && this.grace <= 0) this.crashed(ride, car, BusCrash.Side, airborne);
    if (!car.pressing) return;
    this.pushMeter += dt / r.attack.shoveAfter;
    if (this.pushMeter < 1) return;
    this.pushMeter = 0;
    this.traffic.endAttack(car);
    const away = toward > 0 ? -1 : 1;
    if (player.shoveLane(away)) events.emit('bus:shoved', { ride, car });
    else this.tip(ride, car, away);
  }

  private crashed(ride: BusRide, car: TrafficCar, kind: BusCrash, airborne: boolean): void {
    const { config, events } = this.deps;
    this.grace = config.cargo.grace;
    events.emit('bus:crashed', { ride, car, kind, airborne });
    this.loseCargo(ride, crashLoss(config.cargo, { headOn: kind === BusCrash.HeadOn, airborne }, ride.cargo), CargoLoss.Crash);
  }

  /**
   * Shoved off the edge of the road: the van tips over (user: "der eigene Bulli fällt um, dann blinkt er und kommt wieder
   * ins Spiel – man verliert Flaschen"). Bottles and load lost, the rival has won and drives off, the van blinks.
   */
  private tip(ride: BusRide, car: TrafficCar, side: -1 | 1): void {
    const { config, events, score } = this.deps;
    this.tipT = config.tip.seconds;
    this.tipSide = side;
    this.pushMeter = 0;
    const bottles = score.removeBottles(config.tip.penalty);
    this.traffic.release(car);
    events.emit('bus:tipped', { ride, side, bottles });
    this.loseCargo(ride, Math.min(ride.cargo, config.tip.cargo), CargoLoss.Crash);
  }
  /** The rival is out of the chase: shoved off or crashed (bonus) – or past the van (penalty). */
  private rivalOut(ride: BusRide, car: TrafficCar, outcome: RivalOutcome): void {
    const { score, config, events, maxExtraLives } = this.deps;
    const r = config.traffic.rival;
    let bottles: number;
    if (outcome === RivalOutcome.Passed) {
      bottles = -score.removeBottles(r.passPenalty);
    } else {
      bottles = r.shoveBonus;
      const lives = score.addBonusBottles(bottles, maxExtraLives);
      if (lives > 0) events.emit('score:extraLife', { extraLives: score.snapshot().extraLives });
    }
    events.emit('bus:rivalOut', { ride, car, outcome, bottles });
  }

  /** Pieces fly off the roof – the ride goes on even with an empty rack. */
  private loseCargo(ride: BusRide, pieces: number, reason: CargoLoss): void {
    if (pieces <= 0 || ride.phase !== BusPhase.Driving || ride.cargo <= 0) return;
    const lost = Math.min(pieces, ride.cargo);
    ride.cargo -= lost;
    this.deps.events.emit('bus:cargoLost', { ride, pieces: lost, reason, left: ride.cargo });
  }

  /** The ride is over (always at its end): the runner hops out, the van brakes, the moving traffic turns off. */
  private finish(ride: BusRide, completed: boolean, hopOut: boolean): void {
    const { player, config, events, score } = this.deps;
    if (hopOut && player.vehicle === this.seat) player.dismount(completed ? config.hopOutSpeed : config.throwOff.speed);
    ride.phase = BusPhase.Done;
    ride.deceleration = config.brakeDeceleration;
    // the runner is on foot among them: moving cars leave the road (crashed ones lie at its side)
    this.traffic.cars.splice(0, this.traffic.cars.length, ...this.traffic.cars.filter((car) => car.wrecked));
    if (completed && ride.cargo > 0) {
      const bottles = ride.cargo * config.cargo.bonusPerPiece;
      const lives = score.addBonusBottles(bottles, this.deps.maxExtraLives);
      events.emit('bus:delivered', { ride, pieces: ride.cargo, bottles });
      if (lives > 0) events.emit('score:extraLife', { extraLives: score.snapshot().extraLives });
    }
    events.emit('bus:left', { ride, completed });
  }

  // ── Done ─────────────────────────────────────────────────────────────────

  private brake(ride: BusRide, dt: number): void {
    const { config, world, groundHeightAt } = this.deps;
    const pose = ride.pose;
    if (pose.airborne) {
      ride.vy -= config.gravity * dt;
      pose.y += ride.vy * dt;
      const ground = groundHeightAt(pose.distance, pose.x) ?? world.groundHeight;
      if (pose.y <= ground) {
        pose.y = ground;
        pose.airborne = false;
        ride.vy = 0;
      }
    }
    if (pose.speed <= 0) return;
    pose.speed = Math.max(0, pose.speed - ride.deceleration * dt);
    const step = pose.speed * dt;
    pose.distance += step;
    pose.travelled += step;
    if (!pose.airborne) pose.y = groundHeightAt(pose.distance, pose.x) ?? world.groundHeight;
  }
}
